/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Dropdown, IconButton, Title } from '@cambridgeassessment/cambridge-ui';
import { Component, ConsortiumComponent } from '@cambridgeassessment/lib-ee-commons';
import { useDispatch, useSelector } from 'react-redux';
import thunks from '../../../store/components/thunks';
import { RootState } from '../../../store';
import { DropdownContainer, StyledInputContainer } from '../Content.styles';
import { capitalize } from '../../../util/string';
import { StyledRow, TextColumn } from './ConsortiaManagement.styles';
import { CancelOutlined } from '@cambridgeassessment/cambridge-ui/build/icons';
import { scienceBlue } from '@cambridgeassessment/cambridge-ui/build/abstracts/colours';

const componenSearchRegEx = /^[a-zA-Z\d]{4}[/][0-9]{2}$/;
interface Props {
  onComponentsChange: (centres: ConsortiumComponent[]) => void;
  components: ConsortiumComponent[];
}

const ComponentsSelection = ({ onComponentsChange, components }: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const {
    userData: { businessUnit },
    components: { component: fethedComponent, isLoading },
    consortiaManagement: { consortium },
  } = useSelector((state: RootState) => state);
  const [component, setComponent] = useState<Component | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dropDownText, setDropDownText] = useState<string>('');

  useEffect(() => {
    if (consortium?.components) onComponentsChange(consortium?.components);
  }, [consortium?.components]);

  useEffect(() => {
    fethedComponent && setComponent(fethedComponent);
  }, [fethedComponent]);

  useEffect(() => {
    if (!isLoading && !component) {
      setDropDownText('No results found.');
    }
  }, [isLoading]);

  const setSelectedComponent = (component: Component) => {
    onComponentsChange([
      {
        assessmentCode: component.assessment.code,
        number: component.number,
        assessmentVersion: component.assessment.version,
      },
      ...components,
    ]);
  };

  const onInputChange = (value: string) => {
    setComponent(null);
    setDropDownText('Enter specification code/component code, e.g. J123/01');
    if (componenSearchRegEx.test(value.trim())) {
      const [assessmentCode, componentNumber] = value.trim().split('/');
      const alreadyExists = components.find(
        (item) => item.number === componentNumber && item.assessmentCode.toUpperCase() === assessmentCode.toUpperCase(),
      );
      if (alreadyExists) {
        setDropDownText('Component is already added');
      } else {
        setDropDownText('Searching for component...');
        dispatch(thunks.getComponentByIdentifiers(businessUnit?.id as string, assessmentCode, componentNumber));
      }
    }
  };

  const onOpen = (): void => {
    setComponent(null);
    setDropDownText('Enter specification code/component code, e.g. J123/01');
    setIsOpen(true);
  };

  const onClose = (): void => {
    setIsOpen(false);
    setComponent(null);
  };

  const removeComponent = (item: ConsortiumComponent): void => {
    const index = components.findIndex(
      (component) => component.number === item.number && component.assessmentCode === item.assessmentCode,
    );
    onComponentsChange(components.slice(0, index).concat(components.slice(index + 1, components.length)));
  };

  const renderComponent = (component: Component): string =>
    `${component.assessment.code}/${capitalize(component.number)}`;

  return (
    <StyledInputContainer>
      <Title>Specification(s) and component(s)</Title>
      <DropdownContainer>
        <Dropdown
          data={component ? [component] : []}
          renderOption={renderComponent}
          noOptionsText={dropDownText}
          renderSelectedOption={renderComponent}
          dataQaId='components-select'
          onOpen={onOpen}
          onClose={onClose}
          onChange={setSelectedComponent}
          onInputChange={onInputChange}
          value={null}
          placeholder='Search for specification and component code'
          readOnly={false}
          showPopUpIcon={false}
          open={isOpen}
        />
      </DropdownContainer>
      {components?.map(({ assessmentCode, number, assessmentVersion }) => (
        <StyledRow key={`${assessmentCode}-${number}`}>
          <TextColumn data-qa-id={`component-${assessmentCode}-${number}`}>
            {assessmentCode}/{number}
          </TextColumn>
          <IconButton
            aria-label='remove component'
            onClick={() => removeComponent({ assessmentCode, number, assessmentVersion })}
            qaId={`remove-component-${assessmentCode}-${number}`}
          >
            <CancelOutlined fill={scienceBlue} />
          </IconButton>
        </StyledRow>
      ))}
    </StyledInputContainer>
  );
};

export default ComponentsSelection;
